import React, { useState } from 'react';
import { Box, Container, TextField, Button, Typography, Alert, Stack, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const Contact: React.FC = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [phone, setPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [inquiryType, setInquiryType] = useState('General Inquiry');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitted(false);

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      setError('Please complete all required fields.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please provide a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/contact-inquiries', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firstName, lastName, email, organization, phone, subject, message, inquiryType })
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to submit inquiry');
      }
      setSubmitted(true);
      // Reset fields (keep email/phone to ease follow-up edits)
      setFirstName('');
      setLastName('');
      setSubject('');
      setMessage('');
      setOrganization('');
      setInquiryType('General Inquiry');
      // Navigate to thank-you page
      navigate('/contact/thank-you');
    } catch (err: any) {
      setError(err.message || 'Failed to submit inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" sx={{ color: '#003366', fontWeight: 700, mb: 1 }}>
        Contact Us
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 3 }}>
        We typically respond within 1–2 business days.
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate aria-labelledby="contact-form-title">
        <Typography id="contact-form-title" variant="h6" sx={{ mb: 2 }}>
          Send us a message
        </Typography>

        <Stack spacing={2}>
          {error && (
            <Alert severity="error" role="alert" aria-live="assertive">{error}</Alert>
          )}
          {submitted && !error && (
            <Alert severity="success" role="status" aria-live="polite">Thank you. Your inquiry was submitted successfully.</Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              fullWidth
              inputProps={{ 'aria-required': 'true' }}
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              fullWidth
              inputProps={{ 'aria-required': 'true' }}
            />
          </Stack>

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            inputProps={{ 'aria-required': 'true' }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Organization (optional)"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              fullWidth
            />
            <TextField
              label="Phone (optional)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
            />
          </Stack>

          <TextField
            select
            label="Inquiry Type"
            value={inquiryType}
            onChange={(e) => setInquiryType(e.target.value)}
            fullWidth
          >
            {['General Inquiry', 'Schedule a Demo', 'Capabilities Briefing', 'Procurement / Contracts', 'Support'].map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            fullWidth
            inputProps={{ 'aria-required': 'true' }}
          />

          <TextField
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            fullWidth
            multiline
            minRows={4}
            inputProps={{ 'aria-required': 'true' }}
          />

          <Box>
            <Button type="submit" disabled={submitting} variant="contained" sx={{ backgroundColor: '#003366', '&:hover': { backgroundColor: '#00264d' } }}>
              {submitting ? 'Sending…' : 'Send Message'}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
};

export default Contact;
